/**
 * Step ids, URL parameters and the entities of the integration setup wizard.
 * Import-free: the e2e suite loads this file directly.
 */

/** One step of the setup wizard, in the order the wizard walks them. */
export const SetupStepId = {
  SYSTEM: 'system',
  BRIDGE: 'bridge',
  CONNECTION: 'connection',
  READINESS: 'readiness',
  CODELISTS: 'codelists',
  ENTITIES: 'entities',
  FIRST_SYNC: 'firstSync'
} as const;

export type SetupStepId = (typeof SetupStepId)[keyof typeof SetupStepId];

/** Whether a value, such as a URL parameter, names a step. */
export function isSetupStepId(value: string | null | undefined): value is SetupStepId {
  return Object.values(SetupStepId).some(id => id === value);
}

/** Every step in order; a direct plugin skips `bridge`, one without codelist groups `codelists`. */
export const SETUP_STEP_ORDER: readonly SetupStepId[] = [
  SetupStepId.SYSTEM,
  SetupStepId.BRIDGE,
  SetupStepId.CONNECTION,
  SetupStepId.READINESS,
  SetupStepId.CODELISTS,
  SetupStepId.ENTITIES,
  SetupStepId.FIRST_SYNC
];

/** Query parameters of `/settings/integrations/setup`. */
export const SetupParam = {
  PLUGIN: 'plugin',
  CONNECTION: 'connection',
  STEP: 'step'
} as const;

export type SetupParam = (typeof SetupParam)[keyof typeof SetupParam];

/** How often the wizard reads the bridges while it waits for one to pair or come online. */
export const SETUP_BRIDGE_POLL_INTERVAL_MS = 5_000;

/** Runs read per page of one connector's history; records-scoped pushes fill pages too. */
export const SETUP_RUNS_PAGE_SIZE = 50;

/** Pages read at most while looking for a connector's latest run of each kind. */
export const SETUP_RUNS_MAX_PAGES = 5;

/** How often the first sync reads its runs while it waits, in case a pushed update was lost. */
export const SETUP_RUNS_POLL_INTERVAL_MS = 10_000;

/** Record creation on an outbound run; mirrors the sync-plugin SDK's key. */
export const ALLOW_CREATE_CONFIG_KEY = 'allowCreate';

/** The canonical entities the wizard sets up, as a plugin names them. */
export const SetupEntityType = {
  PARTY: 'Party',
  INVOICE: 'Invoice'
} as const;

export type SetupEntityType = (typeof SetupEntityType)[keyof typeof SetupEntityType];

/** What one sync run of a connector did: a direction, or a read-back after the push. */
export const SetupRunKind = {
  IN: 'IN',
  OUT: 'OUT',
  READ_BACK: 'READ_BACK'
} as const;

export type SetupRunKind = (typeof SetupRunKind)[keyof typeof SetupRunKind];

/** How long a run request that got no answer waits for a run before it counts as not started. */
export const FIRST_SYNC_NO_ANSWER_GRACE_MS = 120_000;
